import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Tag, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const Doctors = () => {
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        fetchDoctors();
    }, []);

    const fetchDoctors = async () => {
        setLoading(true);
        try {
            const response = await api.get('/medicalseller/doctors');
            setDoctors(response.data.data);
        } catch (error) {
            message.error('Failed to load doctors');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddDoctor = () => {
        form.resetFields();
        setEditMode(false);
        setModalVisible(true);
    };

    const handleEditDoctor = (doctor) => {
        setSelectedDoctor(doctor);
        setEditMode(true);
        form.setFieldsValue(doctor);
        setModalVisible(true);
    };

    const handleDeleteDoctor = async (id) => {
        try {
            await api.delete(`/medicalseller/doctor/${id}`);
            message.success('Doctor deactivated successfully');
            fetchDoctors();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to deactivate doctor');
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editMode) {
                await api.put(`/medicalseller/doctor/${selectedDoctor._id}`, values);
                message.success('Doctor updated successfully');
            } else {
                await api.post('/medicalseller/create-doctor', values);
                message.success('Doctor created successfully');
            }
            setModalVisible(false);
            fetchDoctors();
        } catch (error) {
            message.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const viewDoctorDetails = (doctorId) => {
        navigate(`/medicalseller/doctor/${doctorId}`);
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Specialization',
            dataIndex: 'specialization',
            key: 'specialization',
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => viewDoctorDetails(record._id)}
                        size="small"
                    >
                        View
                    </Button>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEditDoctor(record)}
                        size="small"
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to deactivate this doctor?"
                        onConfirm={() => handleDeleteDoctor(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} size="small">
                            Deactivate
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                <h1>Manage Doctors</h1>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddDoctor}>
                    Add New Doctor
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={doctors}
                loading={loading}
                rowKey="_id"
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={editMode ? 'Edit Doctor' : 'Add New Doctor'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter doctor name' }]}
                    >
                        <Input placeholder="Enter doctor name" />
                    </Form.Item>

                    <Form.Item
                        label="Phone Number"
                        name="phoneNumber"
                        rules={[
                            { required: true, message: 'Please enter phone number' },
                            { pattern: /^[0-9]{10,}$/, message: 'Please enter valid phone number' }
                        ]}
                    >
                        <Input placeholder="Enter phone number" disabled={editMode} />
                    </Form.Item>

                    <Form.Item label="Email" name="email">
                        <Input type="email" placeholder="Enter email (optional)" />
                    </Form.Item>

                    {!editMode && (
                        <Form.Item
                            label="Password"
                            name="password"
                            rules={[{ required: true, message: 'Please enter password' }]}
                        >
                            <Input.Password placeholder="Enter password" />
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Specialization"
                        name="specialization"
                        rules={[{ required: true, message: 'Please enter specialization' }]}
                    >
                        <Input placeholder="e.g. Cardiology, Pediatrics" />
                    </Form.Item>

                    <Form.Item label="Address" name="address">
                        <Input.TextArea placeholder="Enter address" rows={2} />
                    </Form.Item>

                    <Form.Item
                        label="Language"
                        name="language"
                        initialValue="en"
                    >
                        <Select>
                            <Select.Option value="en">English</Select.Option>
                            <Select.Option value="ar">العربية</Select.Option>
                            <Select.Option value="ku">کوردی</Select.Option>
                        </Select>
                    </Form.Item>

                    {editMode && (
                        <Form.Item label="Status" name="isActive" valuePropName="checked">
                            <Select>
                                <Select.Option value={true}>Active</Select.Option>
                                <Select.Option value={false}>Inactive</Select.Option>
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item>
                        <Space style={{ float: 'right' }}>
                            <Button onClick={() => setModalVisible(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit">
                                {editMode ? 'Update' : 'Create'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Doctors;
